import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { set } from "lodash";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Defaults builders
import { Adjustment, ReputationAdjustment, SimpleCommission } from "@/PayPlans/helpers/DefaultSections";

// Layouts
import SingleTiersLoader from "@/Layouts/components/Skeletons/SingleTiersLoader";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Custom components
import RenderColumns from "./RenderColumns";
import ReputationTiers from "@/PayPlans/components/Commons/ReputationTiers";

// MUI
import { Grid, Skeleton, Stack } from "@mui/material";

// Constants
const MAX_COMMISSIONS = 99999;

const renderActions = (props) => {
	const { identifier, actions, content, readOnly, handleSwitchChange } = props;
	return actions.map((element, key) => (
		<CustomizedSwitches
			key={`action_${identifier}_${key}`}
			textRight
			disabled={readOnly}
			checked={Boolean(content?.[element.on])}
			textChecked={element.label}
			textUnChecked={element.label}
			onChange={(value) => handleSwitchChange(element.on, value)}
			tooltip={
				element?.label === "Subject to social media reviews"
					? {
							title: "Dependency on reputation management.",
							placement: "right",
							key: "bonus-allowance-social-media-tooltip",
					  }
					: null
			}
		/>
	));
};
renderActions.propTypes = {
	identifier: PropTypes.string,
	actions: PropTypes.array,
	content: PropTypes.object,
	readOnly: PropTypes.bool,
	handleSwitchChange: PropTypes.func,
};

export default function BonusAllowance(props) {
	const {
		sectionId = "bonusAllowance",
		upperColumns = {
			_id: "",
			actions: [],
			left: null,
			right: null,
		},
		lowerColumns = {
			_id: "",
			actions: [],
			left: null,
			right: null,
		},
		content = {
			commissions: [],
			adjustments: [],
			forgiveness: {
				input: "Unit",
				value: 0,
			},
			applyTiersRetroactively: false,
			subjectToReputation: false,
		},
		useForgivenessInput = false,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	const errorIdentifiers = useMemo(() => {
		const identifiers = {
			upperLeftKey: null,
			upperRightKey: null,
			upperAreaKey: null,
			lowerLeftKey: null,
			lowerRightKey: null,
			lowerAreaKey: null,
		};
		if (upperColumns) {
			identifiers.upperLeftKey = upperColumns?.left?._id || null;
			identifiers.upperRightKey = upperColumns?.right?._id || null;
			identifiers.upperAreaKey = upperColumns?._id || null;
		}
		if (lowerColumns) {
			identifiers.lowerLeftKey = lowerColumns?.left?._id || null;
			identifiers.lowerRightKey = lowerColumns?.right?._id || null;
			identifiers.lowerAreaKey = lowerColumns?._id || null;
		}
		return identifiers;
	}, [upperColumns, lowerColumns]);

	// Hooks
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();
	const upperErrorsMessages = getSectionsErrorMessages([errorIdentifiers.upperLeftKey, errorIdentifiers.upperRightKey]);
	const lowerErrorsMessages = getSectionsErrorMessages([errorIdentifiers.lowerLeftKey, errorIdentifiers.lowerRightKey]);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			if (!useForgivenessInput) {
				delete updatedModel["forgiveness"];
			}
			onActionUpdate?.(sectionId, null, updatedModel);
		}
	};

	// States
	const [innerContent, setContent] = useCallbackState(null, onModelUpdate);

	const updateModel = (model) => {
		wasUpdated.current = true;
		setContent(model);
	};

	const handleSwitchChange = (key, value) => {
		let updated = { ...innerContent };
		if (key) {
			updated = { ...updated, [key]: value };
		}

		if (key === "subjectToReputation") {
			updated["adjustments"] = value ? ReputationAdjustment() : [];
			updated["forgiveness"] = {
				input: "Unit",
				value: 0,
			};
		}
		updateModel(updated);
	};

	const handleUpdateValue = (args) => {
		const { column, row, area, value, delta = 0.5 } = args || {};
		const updated = { ...innerContent };

		updated[column][row][area]["value"] = value ? Number(value) : 0;
		if (updated?.[column]?.[row + 1] && area === "to") {
			updated[column][row + 1]["from"]["value"] = (value ? Number(value) : 0) + delta;
		}
		updateModel(updated);
	};

	const handleForgiveness = (key, value) => {
		const updated = { ...innerContent };
		if (key === "input") {
			set(updated, `forgiveness.value`, 0);
		}
		set(updated, `forgiveness.${key}`, value);
		updateModel(updated);
	};

	const handleAddTier = (key, useValues) => {
		const updated = { ...innerContent };
		const lastIndex = updated?.[key]?.length;

		if (key === "adjustments") {
			updated[key].push(Adjustment(0, 100));
			if (lastIndex) {
				updated[key][lastIndex - 1].to.value = 0;
			}
		} else if (key && useValues?.length) {
			const identifier = useValues[0];
			const commission = SimpleCommission(0, MAX_COMMISSIONS, { identifier, commission: 0, input: "Dollar" });
			updated[key].push(commission);
			if (lastIndex) {
				updated[key][lastIndex - 1].to.value = 0;
			}
		}

		wasUpdated.current = false;
		setContent(updated);
	};

	const handleDeleteTier = (key) => {
		const updated = { ...innerContent };

		if (key === "adjustments") {
			if (updated[key]?.length > 2) {
				updated[key].splice(-1);
				const lastIndex = updated[key].length;
				updated[key][lastIndex - 1].to.value = 100;
			}
		} else if (updated[key]?.length > 1) {
			updated[key].splice(-1);
			const lastIndex = updated[key].length;
			updated[key][lastIndex - 1].to.value = 100;
		}

		updateModel(updated);
	};

	useMemo(() => {
		if (innerContent && errorIdentifiers) {
			validate([
				{
					definition: {
						useKey: errorIdentifiers.upperLeftKey,
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0.5 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerContent?.[errorIdentifiers.upperAreaKey] || [],
				},
				{
					definition: {
						useKey: errorIdentifiers.upperRightKey,
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0 },
								},
							],
						},
						errorsMap: [errorIdentifiers.upperRightKey],
						message: `Incorrect ${errorIdentifiers.upperRightKey} definition`,
						validationType: "SIMPLE",
						specialProperties: {
							noFirstError: true,
						},
					},
					section: innerContent?.[errorIdentifiers.upperAreaKey] || [],
				},
				{
					definition: {
						useKey: errorIdentifiers.lowerLeftKey,
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerContent?.[errorIdentifiers.lowerAreaKey] || [],
				},
				{
					definition: {
						useKey: errorIdentifiers.lowerRightKey,
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0 },
								},
							],
						},
						errorsMap: [errorIdentifiers.lowerRightKey],
						message: `Incorrect ${errorIdentifiers.lowerRightKey} definition`,
						validationType: "SIMPLE",
						specialProperties: {
							noFirstError: true,
						},
					},
					section: innerContent?.[errorIdentifiers.lowerAreaKey] || [],
				},
			]);
		}
	}, [innerContent, errorIdentifiers, validate]);

	useEffect(() => {
		setContent(clone(content));

		return () => {
			setContent(null);
			clearSectionErrors();
			wasUpdated.current = false;
		};
	}, [content, setContent, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12}>
				{loading ? (
					<Grid mt={3}>
						<SingleTiersLoader buttons />
						<Grid item mt={3} ml={"32px"}>
							<Stack direction={"row"} alignItems={"center"} spacing={1}>
								<Skeleton variant="rectangular" width={45} height={22} sx={{ borderRadius: "15px" }} />
								<Skeleton variant="text" width={200} height={20} />
							</Stack>
						</Grid>
					</Grid>
				) : (
					<Grid container px={"32px"}>
						<Grid container item direction="column" justifyContent="flex-end">
							{upperColumns?.actions?.map((element, key) => {
								const switchKey = `switch_upper_columns_${key}`;
								return (
									<Grid item key={switchKey} xs={12}>
										<CustomizedSwitches
											disabled={readOnly}
											textLeft
											textChecked={element.label}
											textUnChecked={element.label}
											checked={Boolean(innerContent?.[element.on])}
											onChange={(value) => handleSwitchChange(element.on, value)}
										/>
									</Grid>
								);
							})}
						</Grid>

						{Boolean(upperColumns?._id && innerContent?.[upperColumns?._id]) && (
							<Grid container item xs={12} my={2}>
								<Stack sx={{ minHeight: "100px" }}>
									<RenderColumns
										content={innerContent}
										columns={upperColumns}
										errors={errors}
										errorsMessages={upperErrorsMessages}
										readOnly={readOnly}
										onAddTier={handleAddTier}
										onDeleteTier={handleDeleteTier}
										onHandleUpdateValue={handleUpdateValue}
									/>
								</Stack>
							</Grid>
						)}

						<Grid container item xs={12}>
							{Boolean(innerContent && lowerColumns) && (
								<Stack>
									<ReputationTiers
										readOnly={readOnly}
										rows={innerContent?.[lowerColumns?._id] || []}
										content={lowerColumns}
										showForgiveness={useForgivenessInput}
										forgiveness={innerContent?.forgiveness}
										showContent={lowerColumns?.actions?.some((item) => {
											if (item.on === "subjectToReputation") {
												return Boolean(innerContent?.[item.on]);
											}
											return false;
										})}
										actions={renderActions({
											identifier: "lower_columns",
											actions: lowerColumns?.actions || [],
											content: innerContent,
											readOnly,
											handleSwitchChange,
										})}
										errors={errors}
										errorsMessages={lowerErrorsMessages}
										onActionUpdate={(positions, area, value) => {
											if (positions) {
												return handleUpdateValue({
													column: lowerColumns?._id,
													row: positions.row,
													delta: 0.01,
													area,
													value,
												});
											}
											return handleForgiveness(area, value);
										}}
										onActionAdd={() => handleAddTier(lowerColumns?._id)}
										onActionDelete={() => handleDeleteTier(lowerColumns?._id)}
									/>
								</Stack>
							)}
						</Grid>
					</Grid>
				)}
			</Grid>
		</Grid>
	);
}
BonusAllowance.propTypes = {
	sectionId: PropTypes.string,
	upperColumns: PropTypes.object,
	lowerColumns: PropTypes.object,
	content: PropTypes.object,
	useForgivenessInput: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
